import { z } from 'zod';
import { ESTADOS_LICITACION, MODALIDADES_MONTO, MOTIVOS_PERDIDA, PERFILES_COMPETIDOR, PRIORIDADES, TIPOS_ORGANISMO, TIPOS_PROCESO, } from '../dominio/enums.js';
import { diasSchema, emailSchema, enumDe, fechaOpcional, idSchema, montoSchema, paginacionSchema, porcentajeSchema, rutSchema, textoOpcional, textoRequerido, } from './comunes.js';
// ─────────────────────────────────────────────────────────────
// Organismos y contactos
// ─────────────────────────────────────────────────────────────
export const crearOrganismoSchema = z.object({
    nombre: textoRequerido(250, 'El nombre del organismo'),
    rut: rutSchema,
    tipo: enumDe(TIPOS_ORGANISMO).default('OTRO'),
    region: textoOpcional(100),
    comuna: textoOpcional(100),
    direccion: textoOpcional(255),
    sitioWeb: z.string().trim().url('URL inválida').max(255).optional().or(z.literal('')),
    notas: textoOpcional(4000),
});
export const actualizarOrganismoSchema = crearOrganismoSchema.partial();
export const crearContactoSchema = z.object({
    organismoId: idSchema,
    nombre: textoRequerido(150, 'El nombre del contacto'),
    cargo: textoOpcional(150),
    email: emailSchema.optional().or(z.literal('')),
    telefono: textoOpcional(50),
    notas: textoOpcional(2000),
});
export const actualizarContactoSchema = crearContactoSchema.omit({ organismoId: true }).partial();
// ─────────────────────────────────────────────────────────────
// Competidores
// ─────────────────────────────────────────────────────────────
export const crearCompetidorSchema = z.object({
    nombre: textoRequerido(250, 'El nombre del competidor'),
    rut: rutSchema,
    sitioWeb: z.string().trim().url('URL inválida').max(255).optional().or(z.literal('')),
    notas: textoOpcional(4000),
});
export const actualizarCompetidorSchema = crearCompetidorSchema.partial();
// ─────────────────────────────────────────────────────────────
// Categorías
// ─────────────────────────────────────────────────────────────
export const crearCategoriaSchema = z.object({
    nombre: textoRequerido(100, 'El nombre de la categoría'),
    color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Debe ser un color hexadecimal, por ejemplo #2196F3')
        .default('#2196F3'),
    /** Margen que se quiere conservar en esta línea de negocio. Alimenta el piso de precio. */
    margenObjetivoPct: porcentajeSchema.default(40),
    activa: z.boolean().default(true),
});
export const actualizarCategoriaSchema = crearCategoriaSchema.partial();
// ─────────────────────────────────────────────────────────────
// Licitaciones
// ─────────────────────────────────────────────────────────────
export const baseCrearLicitacionSchema = z.object({
    /** Código de Mercado Público, por ejemplo `1002588-103-SE26`. */
    codigoMercadoPublico: textoRequerido(60, 'El código de Mercado Público'),
    nombre: textoRequerido(300, 'El nombre del proyecto'),
    descripcion: textoOpcional(4000),
    organismoId: idSchema.optional(),
    /** Si no se proporciona `organismoId`, se busca o crea el organismo con este nombre. */
    nombreOrganismo: textoOpcional(250),
    categoriaId: idSchema,
    tipoProceso: enumDe(TIPOS_PROCESO).default('LICITACION'),
    estado: enumDe(ESTADOS_LICITACION).default('OPORTUNIDAD'),
    prioridad: enumDe(PRIORIDADES).default('MEDIA'),
    /** Modalidad preferida para ingresar/visualizar esta licitación. */
    modalidadMonto: enumDe(MODALIDADES_MONTO).default('BRUTO'),
    /** Presupuesto oficial publicado, **neto**. */
    presupuestoOficialNeto: montoSchema.default(0),
    /** Presupuesto oficial publicado, **bruto** (con IVA). */
    presupuestoOficialBruto: montoSchema.optional(),
    /** Lo que ofertamos, **neto**. */
    montoOfertadoNeto: montoSchema.default(0),
    /** Lo que ofertamos, **bruto** (con IVA). */
    montoOfertadoBruto: montoSchema.optional(),
    fechaPublicacion: fechaOpcional,
    fechaCierrePostulacion: fechaOpcional,
    fechaAdjudicacion: fechaOpcional,
    plazoEntregaOfertadoDias: diasSchema.default(0),
    plazoMaximoBasesDias: diasSchema.default(0),
    duracionMeses: z.number().int().min(1).max(120).default(1),
    responsableId: idSchema.optional().nullable(),
    urlMercadoPublico: z.string().trim().url('URL inválida').max(500).optional().or(z.literal('')),
    cantidadOferentes: z.number().int().min(0).max(500).optional().nullable(),
    motivoPerdida: enumDe(MOTIVOS_PERDIDA).optional().nullable(),
    notas: textoOpcional(4000),
});
export const crearLicitacionSchema = baseCrearLicitacionSchema.refine((data) => Boolean(data.organismoId || (data.nombreOrganismo && data.nombreOrganismo.trim().length > 0)), {
    message: 'Debe indicar el organismo comprador (organismoId o nombreOrganismo).',
    path: ['organismoId'],
});
export const actualizarLicitacionSchema = baseCrearLicitacionSchema.partial();
/**
 * Cambio de estado en el pipeline.
 *
 * Se separa de la actualización general porque adjudicar dispara efectos: crea
 * el proyecto, su cronograma de facturación y su presupuesto de costos. Eso
 * merece un endpoint explícito y no esconderse dentro de un PATCH genérico.
 */
export const cambiarEstadoLicitacionSchema = z.object({
    estado: enumDe(ESTADOS_LICITACION),
    /** Modalidad en la que se ingresa el monto adjudicado. */
    modalidadMonto: enumDe(MODALIDADES_MONTO).optional(),
    /** Monto finalmente adjudicado, neto. */
    montoAdjudicadoNeto: montoSchema.optional(),
    /** Monto finalmente adjudicado, bruto (con IVA incluido). */
    montoAdjudicadoBruto: montoSchema.optional(),
    fechaAdjudicacion: fechaOpcional,
    motivoPerdida: enumDe(MOTIVOS_PERDIDA).optional(),
    /** Datos del ganador. Se registran al marcar `PERDIDA`. */
    ganador: z
        .object({
        competidorId: idSchema.optional(),
        nombreCompetidor: textoOpcional(250),
        montoNeto: montoSchema,
        plazoDias: diasSchema.optional(),
    })
        .optional(),
});
/** Oferta de un competidor en un proceso. Enriquece el modelo de precio. */
export const registrarOfertaCompetidorSchema = z.object({
    licitacionId: idSchema,
    competidorId: idSchema.optional(),
    /** Alternativa al id: si el competidor no existe todavía, se crea con este nombre. */
    nombreCompetidor: textoOpcional(250),
    montoNeto: montoSchema,
    plazoDias: diasSchema.optional(),
    esGanador: z.boolean().default(false),
    puntaje: z.number().min(0).max(1000).optional().nullable(),
});
export const filtrosLicitacionSchema = paginacionSchema.extend({
    busqueda: z.string().trim().max(200).optional(),
    estado: enumDe(ESTADOS_LICITACION).optional(),
    categoriaId: idSchema.optional(),
    organismoId: idSchema.optional(),
    tipoProceso: enumDe(TIPOS_PROCESO).optional(),
    responsableId: idSchema.optional(),
    desde: fechaOpcional,
    hasta: fechaOpcional,
});
export const filtrosPipelineSchema = z.object({
    busqueda: z.string().trim().max(200).optional(),
    sector: enumDe(TIPOS_ORGANISMO).optional(),
    categoriaId: idSchema.optional(),
    responsableId: idSchema.optional(),
    soloVencimientoProximo: z.coerce.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────
// Competencia & Inteligencia de Mercado
// ─────────────────────────────────────────────────────────────
export const filtrosCompetenciaSchema = z.object({
    sector: enumDe(TIPOS_ORGANISMO).optional(),
    categoriaId: idSchema.optional(),
    busqueda: z.string().trim().max(200).optional(),
    ordenarPor: z
        .enum(['victorias', 'brechaPrecio', 'montoGanado', 'alfabetico'])
        .default('victorias'),
});
export const registrarActaLicitacionSchema = z.object({
    licitacionId: idSchema,
    competidorId: idSchema.optional(),
    nombreCompetidor: textoOpcional(250),
    montoNeto: montoSchema,
    plazoDias: diasSchema.optional(),
    esGanador: z.boolean().default(true),
    puntaje: z.number().min(0).max(1000).optional().nullable(),
    motivoPerdida: enumDe(MOTIVOS_PERDIDA).optional().default('PRECIO'),
    observaciones: textoOpcional(2000),
});
export const fusionarCompetidoresSchema = z.object({
    competidorOrigenId: idSchema,
    competidorDestinoId: idSchema,
});
export const simularOfertaCompetidorSchema = z.object({
    presupuestoOficialNeto: z.coerce
        .number({ invalid_type_error: 'Debe ser un número' })
        .finite('Monto inválido')
        .min(1, 'El presupuesto oficial neto debe ser mayor a 0')
        .transform((valor) => Math.round(valor)),
    competidorId: idSchema.optional(),
    perfil: enumDe(PERFILES_COMPETIDOR).optional(),
    costoDirectoNeto: z.coerce
        .number({ invalid_type_error: 'Debe ser un número' })
        .finite('Monto inválido')
        .min(0, 'El costo directo no puede ser negativo')
        .transform((valor) => Math.round(valor))
        .optional(),
    margenMinimoPct: z.coerce
        .number({ invalid_type_error: 'Debe ser un porcentaje' })
        .min(0, 'No puede ser negativo')
        .max(100, 'No puede superar 100%')
        .optional()
        .default(15),
});
//# sourceMappingURL=comercial.js.map